import React from 'react';
import { CloseOutlined, FilePdfOutlined } from '@ant-design/icons';
import ImageViewerModal from './ImageViewerModal';

export interface FilePreviewItem {
  key: string;
  name: string;
  url: string;
  isImage: boolean;
  extLabel?: string;
  badgeText?: string;
  onRemove?: () => void;
}

interface FilePreviewStripProps {
  label?: string;
  items: FilePreviewItem[];
  disabled?: boolean;
  showNames?: boolean;
}

const FilePreviewStrip: React.FC<FilePreviewStripProps> = ({ label, items, disabled = false, showNames = true }) => {
  if (!items.length) return null;

  return (
    <div className="flex-1 min-w-0">
      {label ? <p className="text-xs text-gray-600 mb-1">{label}</p> : null}
      <div className="flex gap-3 overflow-x-auto pb-2">
        {items.map((item) => (
          <div key={item.key} className="relative w-24 shrink-0">
            {item.badgeText && (
              <span className="absolute top-1 left-1 z-20 rounded bg-blue-600 text-white text-[10px] px-1.5 py-0.5 leading-none">
                {item.badgeText}
              </span>
            )}
            {!disabled && item.onRemove && (
              <button
                type="button"
                className="absolute top-1 right-1 z-20 h-6 w-6 rounded-full bg-red-600 text-white border border-white shadow-md flex items-center justify-center hover:bg-red-700"
                onClick={item.onRemove}
                title={`Remove ${item.name}`}
                aria-label={`Remove ${item.name}`}
              >
                <CloseOutlined style={{ fontSize: 12 }} />
              </button>
            )}
            {item.isImage ? (
              <ImageViewerModal
                imageUrls={[item.url]}
                title={item.name}
                triggerMode="none"
                trigger={
                  <img
                    src={item.url}
                    alt={item.name}
                    className="h-20 w-24 object-cover rounded border border-gray-200 bg-gray-50 cursor-pointer"
                  />
                }
              />
            ) : (
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="block h-20 w-24 rounded border border-gray-200 overflow-hidden bg-gray-50 cursor-pointer"
                title={item.name}
              >
                <div className="h-full w-full flex items-center justify-center text-xs font-medium text-gray-600 px-1 text-center">
                  {item.extLabel === 'PDF' ? (
                    <span className="flex flex-col items-center gap-1 text-red-600">
                      <FilePdfOutlined style={{ fontSize: 26 }} />
                      <span className="text-[10px] font-semibold leading-none">PDF</span>
                    </span>
                  ) : (
                    item.extLabel || 'FILE'
                  )}
                </div>
              </a>
            )}
            {showNames ? (
              <p className="text-[11px] text-gray-600 mt-1 truncate" title={item.name}>
                {item.name}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FilePreviewStrip;
